import { ChevronDown } from "lucide-react";
import { useId, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn, Skeleton } from "@nexus/ui";
import { ADMIN_LABEL_CLASS } from "./admin-layout-tokens";

interface AdminChartPanelProps {
  title: string;
  /**
   * Series keys and any reference line the chart draws. Only this wraps when
   * the header runs out of room — moving the controls to a second line the
   * moment the legend outgrows one gives an operator a control that changes
   * position with the data, which is a control nobody finds twice.
   */
  legend?: ReactNode;
  /** What the chart is grouped or bucketed by. Always rightmost, never wraps. */
  controls?: ReactNode;
  /** The plot's height in pixels, which the loading and empty states match. */
  height: number;
  isLoading: boolean;
  /** Nothing to plot, as opposed to nothing loaded yet. */
  isEmpty: boolean;
  emptyMessage: string;
  children: ReactNode;
}

/**
 * The chart region, on every tab: a header that names the series, a body that
 * is exactly one plot tall, and a collapse that gets both out of the way.
 *
 * Collapsing is deliberately not in the URL. It is a reading posture — the
 * operator who wants the table has scrolled past the chart, not linked to a
 * page without one — and putting it in the URL would make it something a
 * shared link imposes on the next reader.
 */
export function AdminChartPanel({
  title,
  legend,
  controls,
  height,
  isLoading,
  isEmpty,
  emptyMessage,
  children,
}: AdminChartPanelProps) {
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const bodyId = useId();

  return (
    <section className="shrink-0 rounded-md border border-border bg-card">
      <div className="flex items-center gap-x-4 px-3 py-2">
        <button
          type="button"
          onClick={() => setIsCollapsed((prev) => !prev)}
          aria-expanded={!isCollapsed}
          aria-controls={bodyId}
          className={cn(
            ADMIN_LABEL_CLASS,
            "flex shrink-0 items-center gap-1 rounded-sm outline-none transition-colors hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring",
          )}
        >
          <ChevronDown
            className={cn(
              "size-3.5 transition-transform duration-150 motion-reduce:transition-none",
              isCollapsed && "-rotate-90",
            )}
            aria-hidden
          />
          {title}
        </button>

        {isCollapsed ? null : (
          <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-x-3 gap-y-1">
            {legend}
          </div>
        )}
        {isCollapsed ? null : <div className="shrink-0">{controls}</div>}
      </div>

      {isCollapsed ? null : (
        <div id={bodyId} className="px-1 pb-2 pt-1">
          {isLoading ? (
            <Skeleton className="w-full rounded-sm" style={{ height }} />
          ) : isEmpty ? (
            <div
              className="flex items-center justify-center text-xs text-muted-foreground"
              style={{ height }}
            >
              {emptyMessage}
            </div>
          ) : (
            children
          )}
        </div>
      )}

      {isCollapsed ? (
        <p className="px-3 pb-2 text-xs text-muted-foreground">
          {t("admin.layout.chart.collapsed", "Chart hidden")}
        </p>
      ) : null}
    </section>
  );
}
